/**
 * CAPA data layer — API integration (`apiClient` + `API_ENDPOINTS.CAPA`).
 */
import {
  CAPA_STAGES,
  getNextStatusAfterAction,
  INITIAL_CAPA_STATUS,
  WORKFLOW_ACTIONS,
} from "./capaWorkflow";
import { stripHtml } from "../../../utils/avatarUtils";
import apiClient from "../../../services/apiClient";
import { API_ENDPOINTS } from "../../../config/apiEndpoints";

function getFirstAvailableTable(data, candidates = []) {
  if (!data || typeof data !== "object") return null;
  const keys = Object.keys(data);
  for (const key of candidates) {
    const val = data[key];
    if (Array.isArray(val)) return val;
    const ci = keys.find((k) => k.toLowerCase() === String(key || "").toLowerCase());
    if (ci && Array.isArray(data[ci])) return data[ci];
  }
  const firstArrayKey = keys.find((k) => Array.isArray(data[k]));
  return firstArrayKey ? data[firstArrayKey] : null;
}

function getApiDataset(response) {
  if (!response || typeof response !== "object") return null;
  const d = response.data ?? response.Data;
  return d && typeof d === "object" ? d : null;
}

/** When GetById returns a dataset, use AuditLog only if that table key exists (avoid grabbing wrong arrays). */
function getAuditLogSliceFromCapaGetByIdDataset(ds) {
  if (!ds || typeof ds !== "object") return undefined;
  const keys = Object.keys(ds);
  const key = keys.find((k) => k.toLowerCase() === "auditlog");
  if (!key || !Array.isArray(ds[key])) return undefined;
  return ds[key];
}

/** When GetById returns a dataset, use CapaActionItems only if that table key exists. */
function getActionItemsSliceFromCapaGetByIdDataset(ds) {
  if (!ds || typeof ds !== "object") return undefined;
  const keys = Object.keys(ds);
  const key = keys.find((k) => k.toLowerCase() === "capaactionitems");
  if (!key || !Array.isArray(ds[key])) return undefined;
  return ds[key];
}

/** Map Activity proc / JSON (camelCase) rows to the shape Action Planning UI expects. */
function normalizeCapaActionItemRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r) => ({
    ...r,
    ActionItemId: r.ActionItemId ?? r.actionItemId ?? "",
    Type: r.Type ?? r.type ?? "",
    Description: r.Description ?? r.description ?? "",
    OwnerStaffId: r.OwnerStaffId ?? r.ownerStaffId ?? "",
    OwnerName: r.OwnerName ?? r.ownerName ?? "",
    StartDate: r.StartDate ?? r.startDate ?? null,
    DueDate: r.DueDate ?? r.dueDate ?? null,
    Status: r.Status ?? r.status ?? "",
    DecisionReason: r.DecisionReason ?? r.decisionReason ?? "",
  }));
}

function parseConfigList(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((v) => String(v || "").trim())
      .filter(Boolean);
  } catch (_e) {
    return [];
  }
}

function normalizeAuditRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r) => {
    const actor =
      r?.UserName ||
      r?.userName ||
      r?.PerformedByName ||
      r?.performedByName ||
      r?.CreatedByName ||
      r?.createdByName ||
      r?.PerformedBy ||
      r?.performedBy ||
      r?.CreatedBy ||
      r?.createdBy ||
      "";
    return {
      ...r,
      Id: r?.Id || r?.id || r?.AuditId || r?.auditId || "",
      CapaNo: r?.CapaNo ?? r?.capaNo ?? "",
      At:
        r?.At ||
        r?.at ||
        r?.PerformedAt ||
        r?.performedAt ||
        r?.CreatedAt ||
        r?.createdAt ||
        null,
      UserName: actor ? String(actor) : "",
      Action: r?.Action || r?.action || "Update",
      FieldName: r?.FieldName || r?.fieldName || "",
      OldValue: r?.OldValue ?? r?.oldValue ?? "",
      NewValue: r?.NewValue ?? r?.newValue ?? "",
      Comments: r?.Comments ?? r?.comments ?? "",
    };
  });
}

function isGuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );
}

/** List/detail rows from ASP.NET may use camelCase; UI expects PascalCase. */
function pickCapaId(row) {
  if (!row || typeof row !== "object") return null;
  const v = row.CapaId ?? row.capaId;
  if (v == null || String(v).trim() === "") return null;
  return String(v).trim();
}

function mergeCapaCanonicalFields(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (obj.CapaId == null && obj.capaId != null) obj.CapaId = obj.capaId;
  if (
    obj.OrganizationRefId == null &&
    obj.organizationRefId != null
  ) {
    obj.OrganizationRefId = obj.organizationRefId;
  }
  if (
    (obj.CapaNo == null || String(obj.CapaNo).trim() === "") &&
    obj.capaNo != null
  ) {
    obj.CapaNo = obj.capaNo;
  }
  if (obj.ModifiedAt == null && obj.modifiedAt != null) obj.ModifiedAt = obj.modifiedAt;
  if (obj.Status == null && obj.status != null) obj.Status = obj.status;
  if (obj.RcaMethod == null && obj.rcaMethod != null) obj.RcaMethod = obj.rcaMethod;
  if (String(obj.Status || "").trim() === "In Progress") {
    obj.Status = "Implementation";
  }
  return obj;
}

/** CAPA module SPs scope by OrganizationRefId — prefer value from loaded CAPA row. */
function organizationIdForCapaModule(userData, opts = {}) {
  const fromOpts =
    opts.organizationId ??
    opts.OrganizationRefId ??
    opts.organizationRefId;
  if (fromOpts != null && String(fromOpts).trim() !== "") {
    return String(fromOpts).trim();
  }
  return String(userData?.OrganizationId || "").trim();
}

/** Pass into module APIs (approvers, action items, etc.) so OrganizationId matches the CAPA row. */
export function capaModuleOptsFromDetail(detailRow, userData) {
  return {
    organizationId: organizationIdForCapaModule(userData, {
      organizationId:
        detailRow?.OrganizationRefId ?? detailRow?.organizationRefId,
    }),
  };
}

/** Module stored procs use UNIQUEIDENTIFIER @CapaId — resolve CapaNo (or bad casing) to GUID. */
async function toCapaGuidForModuleApi(capaIdOrNo, userData) {
  const raw = String(capaIdOrNo || "").trim();
  if (!raw || !userData?.OrganizationId) return null;
  if (isGuidLike(raw)) return raw;
  return resolveCapaIdFromRouteValue(raw, userData);
}

function normalizeIdToken(value) {
  return decodeURIComponent(String(value || "").trim())
    .replace(/[{}]/g, "")
    .toLowerCase();
}

async function resolveCapaIdFromRouteValue(capaIdOrNo, userData) {
  const raw = decodeURIComponent(String(capaIdOrNo || "").trim());
  if (!raw) return null;
  if (isGuidLike(raw)) return raw;
  const res = await postCapa(API_ENDPOINTS.CAPA.LIST, {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    IsActive: 1,
  });
  const ds = getApiDataset(res);
  const rows = getFirstAvailableTable(ds, ["CapaList"]);
  if (!Array.isArray(rows)) return null;
  const match = rows.find(
    (r) =>
      String(r?.CapaNo ?? r?.capaNo ?? "")
        .trim()
        .toLowerCase() === raw.toLowerCase(),
  );
  return pickCapaId(match) || null;
}

function buildDetailFromListRow(row = {}, userData) {
  return resolveDetailForUser(
    {
      CapaId: pickCapaId(row),
      OrganizationRefId:
        row.OrganizationRefId ?? row.organizationRefId ?? null,
      CapaNo: String(row?.CapaNo ?? row?.capaNo ?? "").trim() || "",
      Title: row.Title || "",
      Description: row.Description || "",
      SourceType: row.SourceType || "",
      SourceRef: row.SourceRef || "",
      Priority: row.Priority || "",
      Risk: row.Risk || "",
      Status: row.Status || "",
      OwnerStaffId: row.OwnerStaffId || null,
      OwnerName: row.OwnerName || "",
      CreatedByStaffId:
        row.CreatedByStaffId ??
        row.createdByStaffId ??
        row.CreatedBy ??
        row.createdBy ??
        null,
      ReviewerStaffId: row.ReviewerStaffId || null,
      ReviewerName: row.ReviewerName || "",
      DateIdentified: row.DateIdentified || null,
      TargetDueDate: row.TargetDueDate || null,
      IsActive: row.IsActive ?? 1,
      CreatedAt: row.CreatedAt || null,
      ModifiedAt: row.ModifiedAt || null,
      Module: row.Module || row.ModuleLabel || null,
      EntityType: row.EntityType || null,
      EntityId: row.EntityId || null,
      EntityLabel: row.ContextLabel || row.EntityLabel || null,
    },
    userData,
  );
}

function normalizeCapaPayload(payload = {}, userData = {}) {
  return {
    OrganizationId: userData?.OrganizationId || payload?.OrganizationId || "",
    CreatedBy: userData?.StaffId || payload?.CreatedBy || "",
    CapaId: payload?.CapaId || null,
    CapaNo: payload?.CapaNo || null,
    Title: payload?.Title ?? payload?.title ?? null,
    Description: payload?.Description ?? payload?.description ?? null,
    SourceType: payload?.SourceType ?? payload?.sourceType ?? null,
    SourceRef: payload?.SourceRef ?? payload?.sourceRef ?? null,
    Priority: payload?.Priority ?? payload?.priority ?? null,
    Risk: payload?.Risk ?? payload?.risk ?? null,
    Status: payload?.Status ?? null,
    OwnerStaffId: payload?.OwnerStaffId ?? payload?.ownerStaffId ?? userData?.StaffId ?? null,
    ReviewerStaffId: payload?.ReviewerStaffId ?? payload?.reviewerStaffId ?? null,
    Module: payload?.Module ?? payload?.module ?? null,
    EntityType: payload?.EntityType ?? payload?.entityType ?? null,
    EntityId: payload?.EntityId ?? payload?.entityId ?? null,
    EntityLabel: payload?.EntityLabel ?? payload?.entityLabel ?? null,
    StudyIdForIsf: payload?.StudyIdForIsf ?? payload?.studyIdForIsf ?? null,
    TemplateCategoryForIsf:
      payload?.TemplateCategoryForIsf ?? payload?.templateCategoryForIsf ?? null,
    DateIdentified: payload?.DateIdentified ?? payload?.dateIdentified ?? null,
    TargetDueDate: payload?.TargetDueDate ?? payload?.targetDueDate ?? null,
    TransitionAction: payload?.TransitionAction ?? null,
    TransitionComment: payload?.TransitionComment ?? null,
    InactiveReason: payload?.InactiveReason ?? payload?.reason ?? null,
    IsActive: payload?.IsActive ?? null,
    InvestigationSummary:
      payload?.InvestigationSummary ?? payload?.investigationSummary ?? null,
    SupportingEvidence:
      payload?.SupportingEvidence ?? payload?.supportingEvidence ?? null,
    SupportingEvidenceFiles:
      payload?.SupportingEvidenceFiles ?? payload?.supportingEvidenceFiles ?? null,
    RootCauseSummary: payload?.RootCauseSummary ?? payload?.rootCauseSummary ?? null,
    RcaMethod: payload?.RcaMethod ?? payload?.rcaMethod ?? null,
    RcaEvidenceFiles: payload?.RcaEvidenceFiles ?? payload?.rcaEvidenceFiles ?? null,
    ClosureRemarks: payload?.ClosureRemarks ?? payload?.closureRemarks ?? null,
    ClosureEvidenceFiles:
      payload?.ClosureEvidenceFiles ?? payload?.closureEvidenceFiles ?? null,
  };
}

async function postCapa(endpoint, payload) {
  const res = await apiClient.post(endpoint, payload);
  if (!res || Number(res.success) !== 1) {
    throw new Error(res?.message || res?.Message || "CAPA API request failed.");
  }
  return res;
}

/** @param {object} row
 * @param {object} [userData]
 */
export function resolveCapaRowForUser(row, userData) {
  if (!row || !userData) return row;
  const out = mergeCapaCanonicalFields({ ...row });
  if (out.OwnerStaffId === "__CURRENT_USER__") {
    out.OwnerStaffId = userData.StaffId;
    out.OwnerName =
      [userData.FirstName, userData.LastName].filter(Boolean).join(" ") ||
      userData.Name ||
      userData.EmailId ||
      "You";
  }
  if (out.CreatedByStaffId === "__CURRENT_USER__") {
    out.CreatedByStaffId = userData.StaffId;
  }
  if (!out.CreatedByStaffId && out.CreatedBy) {
    out.CreatedByStaffId = out.CreatedBy;
  }
  return out;
}

function resolveDetailForUser(detail, userData) {
  if (!detail || !userData) return detail;
  const out = mergeCapaCanonicalFields({ ...detail });
  if (out.OwnerStaffId === "__CURRENT_USER__") {
    out.OwnerStaffId = userData.StaffId;
    out.OwnerName =
      [userData.FirstName, userData.LastName].filter(Boolean).join(" ") ||
      userData.Name ||
      userData.EmailId ||
      "You";
  }
  if (out.CreatedByStaffId === "__CURRENT_USER__") {
    out.CreatedByStaffId = userData.StaffId;
  }
  if (!out.CreatedByStaffId && out.CreatedBy) {
    out.CreatedByStaffId = out.CreatedBy;
  }
  return out;
}

function toDayKey(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildThirtyDayTrend(rows) {
  const labels = [];
  const created = [];
  const closed = [];
  const createdMap = new Map();
  const closedMap = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 29; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = toDayKey(d);
    labels.push(`${d.getMonth() + 1}/${d.getDate()}`);
    createdMap.set(key, 0);
    closedMap.set(key, 0);
  }

  for (const r of rows) {
    const createdKey = toDayKey(r.CreatedAt);
    if (createdMap.has(createdKey)) {
      createdMap.set(createdKey, (createdMap.get(createdKey) || 0) + 1);
    }
    if (String(r.Status || "").trim().toLowerCase() === "closed") {
      const closedKey = toDayKey(r.ModifiedAt || r.TargetDueDate);
      if (closedMap.has(closedKey)) {
        closedMap.set(closedKey, (closedMap.get(closedKey) || 0) + 1);
      }
    }
  }

  for (let i = 0; i < labels.length; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - (29 - i));
    const key = toDayKey(d);
    created.push(createdMap.get(key) || 0);
    closed.push(closedMap.get(key) || 0);
  }

  return { labels, created, closed };
}

function buildEmptyThirtyDayTrend() {
  const labels = [];
  const created = [];
  const closed = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 29; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    labels.push(`${d.getMonth() + 1}/${d.getDate()}`);
    created.push(0);
    closed.push(0);
  }
  return { labels, created, closed };
}

export async function fetchCapaDashboard(userData) {
  try {
    const res = await postCapa(API_ENDPOINTS.CAPA.DASHBOARD, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
    });
    const ds = getApiDataset(res);
    if (!ds) throw new Error("Missing dashboard dataset.");
    const summaryRows = getFirstAvailableTable(ds, ["DashboardSummary"]);
    const statusRows = getFirstAvailableTable(ds, ["StatusBreakdown"]);
    const s = Array.isArray(summaryRows) && summaryRows[0] ? summaryRows[0] : {};
    const stageCounts = {};
    if (Array.isArray(statusRows)) {
      statusRows.forEach((r) => {
        if (r?.Status != null) stageCounts[String(r.Status)] = Number(r.StatusCount || 0);
      });
    }
    let trend30Days = buildEmptyThirtyDayTrend();
    try {
      const listRes = await apiClient.post(API_ENDPOINTS.CAPA.LIST, {
        OrganizationId: userData?.OrganizationId || "",
        CreatedBy: userData?.StaffId || "",
        IsActive: 1,
      });
      if (listRes?.success === 1) {
        const listDs = getApiDataset(listRes);
        const listRows = getFirstAvailableTable(listDs, ["CapaList"]);
        if (Array.isArray(listRows) && listRows.length > 0) {
          trend30Days = buildThirtyDayTrend(
            listRows.map((r) => resolveCapaRowForUser(r, userData)),
          );
        }
      }
    } catch (_ignored) {
      trend30Days = buildEmptyThirtyDayTrend();
    }

    return {
      openCount: Number(s.OpenCount || 0),
      overdueCount: Number(s.OverdueCount || 0),
      closedCount: Number(s.ClosedCount || 0),
      removedCount: 0,
      totalOverdueTaskCount: 0,
      overdueTaskTypes: [],
      overdueActionTaskCount: 0,
      overdueApprovalRequestCount: 0,
      overdueImplementationTaskCount: 0,
      overdueVerificationRequestCount: 0,
      approvalRequestCount: 0,
      closedThisMonth: 0,
      effectivenessPending: 0,
      reopenedCount: 0,
      recentActivity: [],
      stageCounts,
      myAssignments: 0,
      dashboardTaskRows: [],
      overdueTaskRows: [],
      trend30Days,
    };
  } catch (_e) {
    return {
      openCount: 0,
      overdueCount: 0,
      closedCount: 0,
      removedCount: 0,
      totalOverdueTaskCount: 0,
      overdueTaskTypes: [],
      overdueActionTaskCount: 0,
      overdueApprovalRequestCount: 0,
      overdueImplementationTaskCount: 0,
      overdueVerificationRequestCount: 0,
      approvalRequestCount: 0,
      closedThisMonth: 0,
      effectivenessPending: 0,
      reopenedCount: 0,
      recentActivity: [],
      stageCounts: {},
      myAssignments: 0,
      dashboardTaskRows: [],
      overdueTaskRows: [],
      trend30Days: buildEmptyThirtyDayTrend(),
    };
  }
}

export async function fetchCapaList(userData) {
  try {
    const res = await apiClient.post(API_ENDPOINTS.CAPA.LIST, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
      IsActive: 1,
    });
    if (res?.success === 0) {
      const msg = String(res?.message || "").toLowerCase();
      if (msg.includes("no data")) {
        return [];
      }
      throw new Error(res?.message || "CAPA list API request failed.");
    }
    if (!res || res.success !== 1) {
      throw new Error(res?.message || "CAPA list API request failed.");
    }
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["CapaList"]);
    if (!Array.isArray(rows)) throw new Error("Missing CAPA list data.");
    return rows.map((r) => resolveCapaRowForUser(r, userData));
  } catch (_e) {
    return [];
  }
}

export async function fetchRemovedCapaList(userData) {
  try {
    const res = await apiClient.post(API_ENDPOINTS.CAPA.LIST, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
      IsActive: 0,
    });
    if (!res || res.success !== 1) return [];
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["CapaList"]);
    return Array.isArray(rows)
      ? rows.map((r) => resolveCapaRowForUser(r, userData))
      : [];
  } catch (_e) {
    return [];
  }
}

/**
 * @returns {{
 *   detail: object | null,
 *   auditLogFromGetById: object[] | null,
 *   actionItemsFromGetById: object[] | null,
 * }}
 * `auditLogFromGetById` / `actionItemsFromGetById` are non-null only when GetById returned those tables in the same payload.
 */
export async function fetchCapaById(capaId, userData) {
  const none = {
    detail: null,
    auditLogFromGetById: null,
    actionItemsFromGetById: null,
  };
  const rawRouteId = decodeURIComponent(String(capaId || "").trim());
  if (!rawRouteId) return none;
  const rawRouteIdNormalized = normalizeIdToken(rawRouteId);
  let fallbackDetail = null;
  let candidateId = rawRouteId;
  try {
    // Build a robust fallback from list first.
    const list = await fetchCapaList(userData);
    const fromList = list.find((r) => {
      const rowCapaId = pickCapaId(r);
      const idMatch = rowCapaId && normalizeIdToken(rowCapaId) === rawRouteIdNormalized;
      const noMatch =
        String(r?.CapaNo ?? r?.capaNo ?? "")
          .trim()
          .toLowerCase() === rawRouteId.trim().toLowerCase();
      return idMatch || noMatch;
    });
    if (fromList) {
      fallbackDetail = buildDetailFromListRow(fromList, userData);
      const listGuid = pickCapaId(fromList);
      if (listGuid) {
        candidateId = listGuid;
      }
    } else {
      const resolvedCapaId = await resolveCapaIdFromRouteValue(rawRouteId, userData);
      if (resolvedCapaId) {
        candidateId = resolvedCapaId;
      }
    }
  } catch (_ignoreListErrors) {
    // keep going with direct GetById attempt
  }

  try {
    const res = await postCapa(API_ENDPOINTS.CAPA.GET_BY_ID, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
      CapaId: candidateId,
    });
    const ds = getApiDataset(res);
    const detailRows = getFirstAvailableTable(ds, ["CapaDetail", "capaDetail"]);
    if (!Array.isArray(detailRows) || !detailRows[0]) {
      return {
        detail: fallbackDetail,
        auditLogFromGetById: null,
        actionItemsFromGetById: null,
      };
    }
    const detail = { ...detailRows[0] };
    const contextRows = getFirstAvailableTable(ds, ["CapaContext", "capaContext"]);
    if (Array.isArray(contextRows) && contextRows[0]) {
      Object.assign(detail, contextRows[0]);
    }
    const rawAudit = getAuditLogSliceFromCapaGetByIdDataset(ds);
    const auditLogFromGetById =
      rawAudit === undefined ? null : normalizeAuditRows(rawAudit);
    const rawActions = getActionItemsSliceFromCapaGetByIdDataset(ds);
    const actionItemsFromGetById =
      rawActions === undefined ? null : normalizeCapaActionItemRows(rawActions);
    return {
      detail: resolveDetailForUser(detail, userData),
      auditLogFromGetById,
      actionItemsFromGetById,
    };
  } catch (_getByIdError) {
    return {
      detail: fallbackDetail,
      auditLogFromGetById: null,
      actionItemsFromGetById: null,
    };
  }
}

export async function fetchCapaActionItems(capaId, userData) {
  if (!userData?.OrganizationId) return [];
  try {
    const capId = await toCapaGuidForModuleApi(capaId, userData);
    if (!capId) return [];
    const res = await postCapa(API_ENDPOINTS.CAPA.ACTION_ITEMS_LIST, {
      OrganizationId: userData.OrganizationId,
      CreatedBy: userData.StaffId || "",
      CapaId: capId,
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["CapaActionItems", "capaActionItems"]);
    return Array.isArray(rows) ? normalizeCapaActionItemRows(rows) : [];
  } catch (_e) {
    return [];
  }
}

export async function createCapaActionItem(capaId, payload, userData) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  await postCapa(API_ENDPOINTS.CAPA.SAVE_ACTION_ITEM, {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    ActionItemType: payload.Type,
    ActionItemDescription: payload.Description,
    ActionItemOwnerStaffId: payload.OwnerStaffId,
    ActionItemStartDate: payload.StartDate || null,
    ActionItemDueDate: payload.DueDate,
    ActionItemStatus: payload.Status ?? "N/A",
  });
  return fetchCapaActionItems(capId, userData);
}

export async function updateCapaActionItemStatus(
  capaId,
  actionItemId,
  status,
  reason,
  userData,
) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  await postCapa(API_ENDPOINTS.CAPA.UPDATE_ACTION_ITEM, {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    ActionItemId: actionItemId,
    ActionItemStatus: status,
    ActionItemDecisionReason: reason || "",
  });
  return fetchCapaActionItems(capId, userData);
}

export async function deleteCapaActionItem(capaId, actionItemId, userData) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  await postCapa(API_ENDPOINTS.CAPA.DELETE_ACTION_ITEM, {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    ActionItemId: actionItemId,
  });
  return fetchCapaActionItems(capId, userData);
}

export async function fetchCapaApproverRecords(capaId, userData, opts = {}) {
  try {
    const capId = await toCapaGuidForModuleApi(capaId, userData);
    if (!capId) return [];
    const orgId = organizationIdForCapaModule(userData, opts);
    const res = await postCapa(API_ENDPOINTS.CAPA.GET_APPROVER_RECORDS, {
      CapaId: capId,
      OrganizationId: orgId,
      CreatedBy: userData?.StaffId || "",
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["CapaApproverRecords", "ApprovalsQueue"]);
    const list = Array.isArray(rows) ? rows : [];
    return list.map((r) => ({
      ...r,
      ApproverRecordId:
        r.ApproverRecordId ??
        r.approverRecordId ??
        r.RecordId ??
        r.recordId ??
        "",
      Description: r.Description ?? r.description ?? "",
      ApproverName: r.ApproverName ?? r.approverName ?? "",
      ApproverStaffId: r.ApproverStaffId ?? r.approverStaffId ?? "",
      ApproverEmailId: r.ApproverEmailId ?? r.approverEmailId ?? "",
      DueDate: r.DueDate ?? r.dueDate ?? null,
      Status: r.Status ?? r.status ?? "",
      DecisionReason: r.DecisionReason ?? r.decisionReason ?? "",
      Role: r.Role ?? r.role ?? "",
    }));
  } catch (_e) {
    return [];
  }
}

/** Normalize any date-like value to yyyy-MM-dd for CAPA module APIs (SQL DATETIME binding). */
export function normalizeCapaApiDateYmd(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const s = value.trim();
    const isoDay = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (isoDay) return `${isoDay[1]}-${isoDay[2]}-${isoDay[3]}`;
    const t = Date.parse(s);
    if (!Number.isNaN(t)) {
      const d = new Date(t);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return s;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return String(value);
}

export async function createCapaApproverRecord(capaId, payload, userData, opts = {}) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const dueYmd = normalizeCapaApiDateYmd(payload?.DueDate);
  const orgId = organizationIdForCapaModule(userData, opts);
  if (!orgId) {
    throw new Error("Organization is required to save an approver record.");
  }
  await postCapa(API_ENDPOINTS.CAPA.INSERT_APPROVER_RECORD, {
    CapaId: capId,
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    ApproverStaffId: payload.ApproverStaffId || "",
    ApproverName: (payload.ApproverName && String(payload.ApproverName).trim()) || "",
    ApproverEmailId: (payload.ApproverEmailId && String(payload.ApproverEmailId).trim()) || "",
    ApproverDueDate: dueYmd,
    ApproverScopeDescription: payload.Description || "",
  });
  return fetchCapaApproverRecords(capId, userData, opts);
}

export async function updateCapaApproverRecordStatus(
  capaId,
  recordId,
  status,
  reason,
  userData,
  opts = {},
) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  await postCapa(API_ENDPOINTS.CAPA.UPDATE_APPROVER_RECORD, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    RecordId: recordId,
    Status: status,
    TransitionComment: reason || "",
  });
  return fetchCapaApproverRecords(capId, userData, opts);
}

export async function deleteCapaApproverRecord(capaId, recordId, userData, opts = {}) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  await postCapa(API_ENDPOINTS.CAPA.DELETE_APPROVER_RECORD, {
    CapaId: capId,
    RecordId: recordId,
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
  });
  return fetchCapaApproverRecords(capId, userData, opts);
}

function normalizeCapaImplementationTaskRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r) => ({
    ...r,
    ImplementationTaskId: r.ImplementationTaskId ?? r.implementationTaskId ?? "",
    Description: r.Description ?? r.description ?? "",
    DueDate: r.DueDate ?? r.dueDate ?? null,
    AssignedStaffId: r.AssignedStaffId ?? r.assignedStaffId ?? "",
    AssignedName: r.AssignedName ?? r.assignedName ?? "",
    ImplementationStatus: r.ImplementationStatus ?? r.implementationStatus ?? "",
    StatusChangeDescription: r.StatusChangeDescription ?? r.statusChangeDescription ?? "",
    StatusUpdatedAt: r.StatusUpdatedAt ?? r.statusUpdatedAt ?? null,
    CreatedAt: r.CreatedAt ?? r.createdAt ?? null,
  }));
}

function normalizeCapaVerificationRequestRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((r) => ({
    ...r,
    VerificationRequestId: r.VerificationRequestId ?? r.verificationRequestId ?? "",
    Description: r.Description ?? r.description ?? "",
    AssignedStaffId: r.AssignedStaffId ?? r.assignedStaffId ?? "",
    AssignedName: r.AssignedName ?? r.assignedName ?? "",
    DueDate: r.DueDate ?? r.dueDate ?? null,
    RequestStatus: (() => {
      const raw = r.RequestStatus ?? r.requestStatus ?? "";
      const t = String(raw).trim().toLowerCase();
      if (t === "pending") return "Pending";
      if (t === "submitted") return "Submitted";
      return raw ? String(raw) : "";
    })(),
    Outcome: (() => {
      const o = r.Outcome ?? r.outcome ?? "";
      const t = String(o).trim().toLowerCase();
      if (t === "effective") return "Effective";
      if (t === "not effective" || t === "not_effective") return "Not Effective";
      return o ? String(o) : "";
    })(),
    VerifierComments: r.VerifierComments ?? r.verifierComments ?? "",
    VerificationAttachments:
      r.VerificationAttachments ?? r.verificationAttachments ?? "",
    RequestedByName: r.RequestedByName ?? r.requestedByName ?? "",
    CreatedAt: r.CreatedAt ?? r.createdAt ?? null,
    SubmittedAt: r.SubmittedAt ?? r.submittedAt ?? null,
  }));
}

export async function fetchCapaImplementationTasks(capaId, userData, opts = {}) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  const res = await postCapa(API_ENDPOINTS.CAPA.IMPLEMENTATION_TASKS_LIST, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
  });
  const ds = getApiDataset(res);
  const rows = getFirstAvailableTable(ds, [
    "CapaImplementationTasks",
    "capaImplementationTasks",
  ]);
  return Array.isArray(rows) ? normalizeCapaImplementationTaskRows(rows) : [];
}

export async function createCapaImplementationTask(capaId, payload, userData, opts = {}) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  await postCapa(API_ENDPOINTS.CAPA.SAVE_IMPLEMENTATION_TASK, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    ImplementationTaskDescription: payload?.Description ?? "",
    ImplementationTaskDueDate: payload?.DueDate ?? null,
    ImplementationTaskAssignedStaffId: payload?.AssignedStaffId ?? "",
    ImplementationTaskAssignedName: payload?.AssignedName ?? "",
    ImplementationTaskStatus: payload?.ImplementationStatus ?? "Not Started",
  });
  return fetchCapaImplementationTasks(capaId, userData, opts);
}

export async function updateCapaImplementationTaskStatus(
  capaId,
  taskId,
  implementationStatus,
  statusDescription,
  userData,
  opts = {},
) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  await postCapa(API_ENDPOINTS.CAPA.UPDATE_IMPLEMENTATION_TASK, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    TaskId: taskId,
    ImplementationTaskStatus: implementationStatus,
    ImplementationTaskStatusDescription: statusDescription || "",
  });
  return fetchCapaImplementationTasks(capaId, userData, opts);
}

export async function deleteCapaImplementationTask(capaId, taskId, userData, opts = {}) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  await postCapa(API_ENDPOINTS.CAPA.DELETE_IMPLEMENTATION_TASK, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    TaskId: taskId,
  });
  return fetchCapaImplementationTasks(capaId, userData, opts);
}

export async function fetchCapaVerificationRequests(capaId, userData, opts = {}) {
  try {
    const capId = await toCapaGuidForModuleApi(capaId, userData);
    if (!capId || !userData?.OrganizationId) return [];
    const orgId = organizationIdForCapaModule(userData, opts);
    const res = await postCapa(API_ENDPOINTS.CAPA.VERIFICATION_REQUESTS_LIST, {
      OrganizationId: orgId,
      CreatedBy: userData?.StaffId || "",
      CapaId: capId,
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, [
      "CapaVerificationRequests",
      "capaVerificationRequests",
    ]);
    return Array.isArray(rows) ? normalizeCapaVerificationRequestRows(rows) : [];
  } catch (_e) {
    return [];
  }
}

export async function createCapaVerificationRequest(capaId, payload, userData, opts = {}) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  const dueYmd = normalizeCapaApiDateYmd(payload?.DueDate ?? payload?.dueDate);
  if (!dueYmd) {
    throw new Error("Due date is required for verification requests.");
  }
  await postCapa(API_ENDPOINTS.CAPA.SAVE_VERIFICATION_REQUEST, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    VerificationRequestDescription: payload?.Description ?? payload?.description ?? "",
    VerificationRequestAssignedStaffId: payload?.AssignedStaffId ?? "",
    VerificationRequestAssignedName: String(payload?.AssignedName ?? "").trim(),
    VerificationRequestDueDate: dueYmd,
  });
  return fetchCapaVerificationRequests(capaId, userData, opts);
}

export async function deleteCapaVerificationRequest(
  capaId,
  requestId,
  userData,
  opts = {},
) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  await postCapa(API_ENDPOINTS.CAPA.DELETE_VERIFICATION_REQUEST, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    VerificationRequestId: requestId,
  });
  return fetchCapaVerificationRequests(capaId, userData, opts);
}

/**
 * Complete a verification request (persist outcome, audit, notify owner via template).
 * Does not advance CAPA lifecycle; owner uses Send for closure when ready.
 */
export async function submitCapaVerificationResponse(
  capaId,
  requestId,
  payload,
  userData,
  opts = {},
) {
  const capId = await toCapaGuidForModuleApi(capaId, userData);
  if (!capId) throw new Error("Could not resolve CAPA identifier.");
  const orgId = organizationIdForCapaModule(userData, opts);
  const outcome = String(
    payload?.Outcome ?? payload?.VerificationRequestOutcome ?? "",
  ).trim();
  await postCapa(API_ENDPOINTS.CAPA.SUBMIT_VERIFICATION_REQUEST, {
    OrganizationId: orgId,
    CreatedBy: userData?.StaffId || "",
    CapaId: capId,
    VerificationRequestId: requestId,
    VerificationRequestOutcome: outcome,
    VerificationVerifierComments: String(
      payload?.VerifierComments ?? payload?.verificationVerifierComments ?? "",
    ).trim(),
    VerificationAttachments: payload?.VerificationAttachments ?? "",
  });
  const requests = await fetchCapaVerificationRequests(capaId, userData, opts);
  return {
    requests,
    transition: { ok: true },
  };
}

export function verificationRequestAssigneeMatches(req, userData) {
  if (!req || !userData?.StaffId) return false;
  if (req.RequestStatus !== "Pending") return false;
  if (req.AssignedStaffId === "__CURRENT_USER__") return true;
  return (
    req.AssignedStaffId &&
    String(req.AssignedStaffId).toLowerCase() ===
      String(userData.StaffId).toLowerCase()
  );
}

function actionItemOwnerMatches(a, userData) {
  if (!userData?.StaffId) return false;
  const sid = String(userData.StaffId).toLowerCase();
  if (a.OwnerStaffId === "__CURRENT_USER__") return true;
  return (
    a.OwnerStaffId &&
    String(a.OwnerStaffId).toLowerCase() === sid
  );
}

/** Action items assigned to the current user (My CAPA tasks). */
export async function fetchCapaTasks(userData) {
  try {
    const res = await postCapa(API_ENDPOINTS.CAPA.TASKS_LIST, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["TasksList"]);
    if (!Array.isArray(rows)) throw new Error("Missing tasks data.");
    return rows;
  } catch (_e) {
    return [];
  }
  return [];
}

/** CAPAs in Action Planning (review queue before approval). */
export async function fetchCapaApprovals(userData) {
  try {
    const res = await postCapa(API_ENDPOINTS.CAPA.APPROVALS_QUEUE, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["ApprovalsQueue"]);
    if (!Array.isArray(rows)) throw new Error("Missing approvals data.");
    return rows;
  } catch (_e) {
    return [];
  }
}

export async function fetchCapaEffectiveness(_filters) {
  try {
    const res = await postCapa(API_ENDPOINTS.CAPA.EFFECTIVENESS_QUEUE, {
      OrganizationId: _filters?.OrganizationId || "",
      CreatedBy: _filters?.StaffId || "",
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["EffectivenessQueue"]);
    if (!Array.isArray(rows)) throw new Error("Missing effectiveness data.");
    return rows;
  } catch (_e) {
    return [];
  }
}

export async function fetchCapaAuditLog(userData, opts = {}) {
  try {
    if (!userData?.OrganizationId) return [];
    const res = await postCapa(API_ENDPOINTS.CAPA.AUDIT_LOG, {
      OrganizationId: userData.OrganizationId,
      CreatedBy: userData.StaffId || "",
      CapaId: opts.CapaId ?? null,
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["AuditLog", "auditLog"]);
    if (!Array.isArray(rows)) throw new Error("Missing audit log data.");
    return normalizeAuditRows(rows);
  } catch (_e) {
    return [];
  }
}

/** Newest first; includes Create, Update, and workflow rows for this CAPA. */
export async function fetchCapaAuditLogForCapa(capaId, userData, opts = {}) {
  try {
    const orgId = organizationIdForCapaModule(userData, opts);
    if (!orgId) return [];
    const capId = await toCapaGuidForModuleApi(capaId, userData);
    if (!capId) return [];
    const res = await postCapa(API_ENDPOINTS.CAPA.AUDIT_LOG, {
      OrganizationId: orgId,
      CreatedBy: userData?.StaffId || "",
      CapaId: capId,
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["AuditLog", "auditLog"]);
    if (!Array.isArray(rows)) throw new Error("Missing CAPA audit log.");
    return normalizeAuditRows(rows);
  } catch (_e) {
    return [];
  }
}

/** CAPAConfig API row may be PascalCase or camelCase after JSON deserialization. */
function pickCapaConfigField(row, pascalKey, camelKey) {
  if (!row || typeof row !== "object") return undefined;
  if (row[pascalKey] !== undefined && row[pascalKey] !== null) return row[pascalKey];
  if (row[camelKey] !== undefined && row[camelKey] !== null) return row[camelKey];
  const want = String(pascalKey || "").toLowerCase();
  const hit = Object.keys(row).find((k) => k.toLowerCase() === want);
  return hit !== undefined ? row[hit] : undefined;
}

function capaConfigStr(row, pascalKey, camelKey) {
  const v = pickCapaConfigField(row, pascalKey, camelKey);
  if (v == null) return "";
  return String(v).trim();
}

/** When GetConfig returns no row, API error, or JSON shape we cannot parse — keep UI usable (aligns with SQL seed defaults). */
function buildCapaConfigFallback() {
  const apSubj =
    "CAPA {{CapaNo}} — action planning task assigned to {{OwnerName}}";
  const apBody =
    "<p>Hello {{OwnerName}},</p><p>You have been assigned an action planning task on CAPA {{CapaNo}}.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Task type:</strong> {{Type}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Task details:</strong></p><p>{{Description}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p><p>Regards</p>";
  const accSubj =
    "CAPA {{CapaNo}} — action planning task accepted by {{DecidedByName}}";
  const accBody =
    "<p>Hello,</p><p><strong>{{DecidedByName}}</strong> accepted an action planning task on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Task assignee:</strong> {{TaskAssigneeName}}<br /><strong>Task type:</strong> {{Type}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Decision reason:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  const rejSubj =
    "CAPA {{CapaNo}} — action planning task rejected by {{DecidedByName}}";
  const rejBody =
    "<p>Hello,</p><p><strong>{{DecidedByName}}</strong> rejected an action planning task on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Task assignee:</strong> {{TaskAssigneeName}}<br /><strong>Task type:</strong> {{Type}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Decision reason:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  const asgSubj = "CAPA {{CapaNo}} — approver: {{ApproverName}}";
  const asgBody =
    "<p>Hello {{ApproverName}},</p><p>You have been assigned as an approver on CAPA {{CapaNo}}.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Description:</strong> {{Description}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p><p>Regards</p>";
  const okSubj = "CAPA {{CapaNo}} — approval accepted by {{DecidedByName}}";
  const okBody =
    "<p>Hello {{OwnerName}},</p><p><strong>{{DecidedByName}}</strong> accepted an approval request on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Approver:</strong> {{ApproverName}}<br /><strong>Description:</strong> {{Description}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Decision reason:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  const noSubj = "CAPA {{CapaNo}} — approval rejected by {{DecidedByName}}";
  const noBody =
    "<p>Hello {{OwnerName}},</p><p><strong>{{DecidedByName}}</strong> rejected an approval request on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Approver:</strong> {{ApproverName}}<br /><strong>Description:</strong> {{Description}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Decision reason:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  const implAsgSubj = "CAPA {{CapaNo}} — implementation task assigned to {{OwnerName}}";
  const implAsgBody =
    "<p>Hello {{OwnerName}},</p><p>You have been assigned an implementation task on CAPA {{CapaNo}}.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Task details:</strong></p><p>{{Description}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p><p>Regards</p>";
  const implOwnAccSubj = "CAPA {{CapaNo}} — implementation status updated by {{DecidedByName}}";
  const implOwnAccBody =
    "<p>Hello {{OwnerName}},</p><p><strong>{{DecidedByName}}</strong> updated an implementation task on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Previous status:</strong> {{PreviousStatus}}<br /><strong>New status:</strong> {{NewStatus}}</p><p><strong>Task assignee:</strong> {{TaskAssigneeName}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Status comment:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  const verAsgSubj = "CAPA {{CapaNo}} — verification request assigned to {{OwnerName}}";
  const verAsgBody =
    "<p>Hello {{OwnerName}},</p><p>You have been assigned a verification task on CAPA {{CapaNo}}.</p><p><strong>Title:</strong> {{Title}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Instructions:</strong></p><p>{{Description}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p><p>Regards</p>";
  const verEffSubj =
    "CAPA {{CapaNo}} — verification outcome: Effective ({{DecidedByName}})";
  const verEffBody =
    "<p>Hello {{OwnerName}},</p><p><strong>{{DecidedByName}}</strong> submitted a verification outcome on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Outcome:</strong> {{Decision}}</p><p><strong>Verifier:</strong> {{TaskAssigneeName}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Comments:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  const verNeSubj =
    "CAPA {{CapaNo}} — verification outcome: Not Effective ({{DecidedByName}})";
  const verNeBody =
    "<p>Hello {{OwnerName}},</p><p><strong>{{DecidedByName}}</strong> submitted a verification outcome on CAPA <strong>{{CapaNo}}</strong>.</p><p><strong>Outcome:</strong> {{Decision}}</p><p><strong>Verifier:</strong> {{TaskAssigneeName}}<br /><strong>Due date:</strong> {{DueDate}}</p><p><strong>Comments:</strong></p><p>{{DecisionReason}}</p><p><a href=\"{{CapaUrl}}\" rel=\"noopener noreferrer\">Open CAPA</a></p>";
  return {
    numberingFormat: "CAPA-{YYYY}-{SEQ}",
    defaultSlaDays: 30,
    statuses: [...CAPA_STAGES],
    modules: ["study_management", "user_management", "isf"],
    sourceTypes: [
      "Audit",
      "Deviation",
      "Complaint",
      "Inspection",
      "Risk assessment",
      "Other",
    ],
    rcaMethods: ["5 Whys", "Fishbone", "Pareto Analysis", "Fault Tree Analysis", "Other"],
    actionPlanningEmailSubjectDefault: apSubj,
    actionPlanningEmailBodyDefault: apBody,
    actionPlanningEmailSubjectTemplate: apSubj,
    actionPlanningEmailBodyTemplate: apBody,
    actionPlanningTaskAcceptEmailSubjectDefault: accSubj,
    actionPlanningTaskAcceptEmailBodyDefault: accBody,
    actionPlanningTaskAcceptEmailSubjectTemplate: accSubj,
    actionPlanningTaskAcceptEmailBodyTemplate: accBody,
    actionPlanningTaskRejectEmailSubjectDefault: rejSubj,
    actionPlanningTaskRejectEmailBodyDefault: rejBody,
    actionPlanningTaskRejectEmailSubjectTemplate: rejSubj,
    actionPlanningTaskRejectEmailBodyTemplate: rejBody,
    approvalAssignmentEmailSubjectDefault: asgSubj,
    approvalAssignmentEmailBodyDefault: asgBody,
    approvalAssignmentEmailSubjectTemplate: asgSubj,
    approvalAssignmentEmailBodyTemplate: asgBody,
    approvalDecisionAcceptEmailSubjectDefault: okSubj,
    approvalDecisionAcceptEmailBodyDefault: okBody,
    approvalDecisionAcceptEmailSubjectTemplate: okSubj,
    approvalDecisionAcceptEmailBodyTemplate: okBody,
    approvalDecisionRejectEmailSubjectDefault: noSubj,
    approvalDecisionRejectEmailBodyDefault: noBody,
    approvalDecisionRejectEmailSubjectTemplate: noSubj,
    approvalDecisionRejectEmailBodyTemplate: noBody,
    implAssignmentEmailSubjectDefault: implAsgSubj,
    implAssignmentEmailBodyDefault: implAsgBody,
    implAssignmentEmailSubjectTemplate: implAsgSubj,
    implAssignmentEmailBodyTemplate: implAsgBody,
    implOwnerAcceptEmailSubjectDefault: implOwnAccSubj,
    implOwnerAcceptEmailBodyDefault: implOwnAccBody,
    implOwnerAcceptEmailSubjectTemplate: implOwnAccSubj,
    implOwnerAcceptEmailBodyTemplate: implOwnAccBody,
    implOwnerRejectEmailSubjectDefault: implOwnAccSubj,
    implOwnerRejectEmailBodyDefault: implOwnAccBody,
    implOwnerRejectEmailSubjectTemplate: implOwnAccSubj,
    implOwnerRejectEmailBodyTemplate: implOwnAccBody,
    verificationAssignmentEmailSubjectDefault: verAsgSubj,
    verificationAssignmentEmailBodyDefault: verAsgBody,
    verificationAssignmentEmailSubjectTemplate: verAsgSubj,
    verificationAssignmentEmailBodyTemplate: verAsgBody,
    verificationOutcomeEffectiveEmailSubjectDefault: verEffSubj,
    verificationOutcomeEffectiveEmailBodyDefault: verEffBody,
    verificationOutcomeEffectiveEmailSubjectTemplate: verEffSubj,
    verificationOutcomeEffectiveEmailBodyTemplate: verEffBody,
    verificationOutcomeNotEffectiveEmailSubjectDefault: verNeSubj,
    verificationOutcomeNotEffectiveEmailBodyDefault: verNeBody,
    verificationOutcomeNotEffectiveEmailSubjectTemplate: verNeSubj,
    verificationOutcomeNotEffectiveEmailBodyTemplate: verNeBody,
  };
}

export async function fetchCapaConfig(userData) {
  try {
    const res = await postCapa(API_ENDPOINTS.CAPA.CONFIG_GET, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
    });
    const ds = getApiDataset(res);
    const rows = getFirstAvailableTable(ds, ["CapaConfig", "capaConfig"]);
    if (!Array.isArray(rows) || !rows[0]) {
      return buildCapaConfigFallback();
    }
    const row = rows[0];
    const subjDef = capaConfigStr(
      row,
      "ActionPlanningEmailSubjectDefault",
      "actionPlanningEmailSubjectDefault",
    );
    const bodyDef = capaConfigStr(
      row,
      "ActionPlanningEmailBodyDefault",
      "actionPlanningEmailBodyDefault",
    );
    const subjTpl = capaConfigStr(
      row,
      "ActionPlanningEmailSubjectTemplate",
      "actionPlanningEmailSubjectTemplate",
    );
    const bodyTpl = capaConfigStr(
      row,
      "ActionPlanningEmailBodyTemplate",
      "actionPlanningEmailBodyTemplate",
    );
    const accSubjDef = capaConfigStr(
      row,
      "ActionPlanningTaskAcceptEmailSubjectDefault",
      "actionPlanningTaskAcceptEmailSubjectDefault",
    );
    const accBodyDef = capaConfigStr(
      row,
      "ActionPlanningTaskAcceptEmailBodyDefault",
      "actionPlanningTaskAcceptEmailBodyDefault",
    );
    const accSubjTpl = capaConfigStr(
      row,
      "ActionPlanningTaskAcceptEmailSubjectTemplate",
      "actionPlanningTaskAcceptEmailSubjectTemplate",
    );
    const accBodyTpl = capaConfigStr(
      row,
      "ActionPlanningTaskAcceptEmailBodyTemplate",
      "actionPlanningTaskAcceptEmailBodyTemplate",
    );
    const rejSubjDef = capaConfigStr(
      row,
      "ActionPlanningTaskRejectEmailSubjectDefault",
      "actionPlanningTaskRejectEmailSubjectDefault",
    );
    const rejBodyDef = capaConfigStr(
      row,
      "ActionPlanningTaskRejectEmailBodyDefault",
      "actionPlanningTaskRejectEmailBodyDefault",
    );
    const rejSubjTpl = capaConfigStr(
      row,
      "ActionPlanningTaskRejectEmailSubjectTemplate",
      "actionPlanningTaskRejectEmailSubjectTemplate",
    );
    const rejBodyTpl = capaConfigStr(
      row,
      "ActionPlanningTaskRejectEmailBodyTemplate",
      "actionPlanningTaskRejectEmailBodyTemplate",
    );

    const apvAssignSubjDef = capaConfigStr(
      row,
      "ApprovalAssignmentEmailSubjectDefault",
      "approvalAssignmentEmailSubjectDefault",
    );
    const apvAssignBodyDef = capaConfigStr(
      row,
      "ApprovalAssignmentEmailBodyDefault",
      "approvalAssignmentEmailBodyDefault",
    );
    const apvAssignSubjTpl = capaConfigStr(
      row,
      "ApprovalAssignmentEmailSubjectTemplate",
      "approvalAssignmentEmailSubjectTemplate",
    );
    const apvAssignBodyTpl = capaConfigStr(
      row,
      "ApprovalAssignmentEmailBodyTemplate",
      "approvalAssignmentEmailBodyTemplate",
    );
    const apvAccSubjDef = capaConfigStr(
      row,
      "ApprovalDecisionAcceptEmailSubjectDefault",
      "approvalDecisionAcceptEmailSubjectDefault",
    );
    const apvAccBodyDef = capaConfigStr(
      row,
      "ApprovalDecisionAcceptEmailBodyDefault",
      "approvalDecisionAcceptEmailBodyDefault",
    );
    const apvAccSubjTpl = capaConfigStr(
      row,
      "ApprovalDecisionAcceptEmailSubjectTemplate",
      "approvalDecisionAcceptEmailSubjectTemplate",
    );
    const apvAccBodyTpl = capaConfigStr(
      row,
      "ApprovalDecisionAcceptEmailBodyTemplate",
      "approvalDecisionAcceptEmailBodyTemplate",
    );
    const apvRejSubjDef = capaConfigStr(
      row,
      "ApprovalDecisionRejectEmailSubjectDefault",
      "approvalDecisionRejectEmailSubjectDefault",
    );
    const apvRejBodyDef = capaConfigStr(
      row,
      "ApprovalDecisionRejectEmailBodyDefault",
      "approvalDecisionRejectEmailBodyDefault",
    );
    const apvRejSubjTpl = capaConfigStr(
      row,
      "ApprovalDecisionRejectEmailSubjectTemplate",
      "approvalDecisionRejectEmailSubjectTemplate",
    );
    const apvRejBodyTpl = capaConfigStr(
      row,
      "ApprovalDecisionRejectEmailBodyTemplate",
      "approvalDecisionRejectEmailBodyTemplate",
    );

    const implAsgSubjDef = capaConfigStr(
      row,
      "ImplAssignmentEmailSubjectDefault",
      "implAssignmentEmailSubjectDefault",
    );
    const implAsgBodyDef = capaConfigStr(
      row,
      "ImplAssignmentEmailBodyDefault",
      "implAssignmentEmailBodyDefault",
    );
    const implAsgSubjTpl = capaConfigStr(
      row,
      "ImplAssignmentEmailSubjectTemplate",
      "implAssignmentEmailSubjectTemplate",
    );
    const implAsgBodyTpl = capaConfigStr(
      row,
      "ImplAssignmentEmailBodyTemplate",
      "implAssignmentEmailBodyTemplate",
    );
    const implOwnAccSubjDef = capaConfigStr(
      row,
      "ImplOwnerAcceptEmailSubjectDefault",
      "implOwnerAcceptEmailSubjectDefault",
    );
    const implOwnAccBodyDef = capaConfigStr(
      row,
      "ImplOwnerAcceptEmailBodyDefault",
      "implOwnerAcceptEmailBodyDefault",
    );
    const implOwnAccSubjTpl = capaConfigStr(
      row,
      "ImplOwnerAcceptEmailSubjectTemplate",
      "implOwnerAcceptEmailSubjectTemplate",
    );
    const implOwnAccBodyTpl = capaConfigStr(
      row,
      "ImplOwnerAcceptEmailBodyTemplate",
      "implOwnerAcceptEmailBodyTemplate",
    );

    const verAsgSubjDef = capaConfigStr(
      row,
      "VerificationAssignmentEmailSubjectDefault",
      "verificationAssignmentEmailSubjectDefault",
    );
    const verAsgBodyDef = capaConfigStr(
      row,
      "VerificationAssignmentEmailBodyDefault",
      "verificationAssignmentEmailBodyDefault",
    );
    const verAsgSubjTpl = capaConfigStr(
      row,
      "VerificationAssignmentEmailSubjectTemplate",
      "verificationAssignmentEmailSubjectTemplate",
    );
    const verAsgBodyTpl = capaConfigStr(
      row,
      "VerificationAssignmentEmailBodyTemplate",
      "verificationAssignmentEmailBodyTemplate",
    );
    const verEffSubjDef = capaConfigStr(
      row,
      "VerificationOutcomeEffectiveEmailSubjectDefault",
      "verificationOutcomeEffectiveEmailSubjectDefault",
    );
    const verEffBodyDef = capaConfigStr(
      row,
      "VerificationOutcomeEffectiveEmailBodyDefault",
      "verificationOutcomeEffectiveEmailBodyDefault",
    );
    const verEffSubjTpl = capaConfigStr(
      row,
      "VerificationOutcomeEffectiveEmailSubjectTemplate",
      "verificationOutcomeEffectiveEmailSubjectTemplate",
    );
    const verEffBodyTpl = capaConfigStr(
      row,
      "VerificationOutcomeEffectiveEmailBodyTemplate",
      "verificationOutcomeEffectiveEmailBodyTemplate",
    );
    const verNeSubjDef = capaConfigStr(
      row,
      "VerificationOutcomeNotEffectiveEmailSubjectDefault",
      "verificationOutcomeNotEffectiveEmailSubjectDefault",
    );
    const verNeBodyDef = capaConfigStr(
      row,
      "VerificationOutcomeNotEffectiveEmailBodyDefault",
      "verificationOutcomeNotEffectiveEmailBodyDefault",
    );
    const verNeSubjTpl = capaConfigStr(
      row,
      "VerificationOutcomeNotEffectiveEmailSubjectTemplate",
      "verificationOutcomeNotEffectiveEmailSubjectTemplate",
    );
    const verNeBodyTpl = capaConfigStr(
      row,
      "VerificationOutcomeNotEffectiveEmailBodyTemplate",
      "verificationOutcomeNotEffectiveEmailBodyTemplate",
    );

    return {
      numberingFormat:
        capaConfigStr(row, "NumberingFormat", "numberingFormat") || "CAPA-{YYYY}-{SEQ}",
      defaultSlaDays: Number(
        pickCapaConfigField(row, "DefaultSlaDays", "defaultSlaDays") ?? 30,
      ),
      statuses: parseConfigList(pickCapaConfigField(row, "StatusesJson", "statusesJson")),
      modules: parseConfigList(pickCapaConfigField(row, "ModulesJson", "modulesJson")),
      sourceTypes: parseConfigList(
        pickCapaConfigField(row, "SourceTypesJson", "sourceTypesJson"),
      ),
      rcaMethods: parseConfigList(pickCapaConfigField(row, "RcaMethodsJson", "rcaMethodsJson")),
      actionPlanningEmailSubjectDefault: subjDef,
      actionPlanningEmailBodyDefault: bodyDef,
      actionPlanningEmailSubjectTemplate: subjTpl || subjDef,
      actionPlanningEmailBodyTemplate: bodyTpl || bodyDef,
      actionPlanningTaskAcceptEmailSubjectDefault: accSubjDef,
      actionPlanningTaskAcceptEmailBodyDefault: accBodyDef,
      actionPlanningTaskAcceptEmailSubjectTemplate: accSubjTpl || accSubjDef,
      actionPlanningTaskAcceptEmailBodyTemplate: accBodyTpl || accBodyDef,
      actionPlanningTaskRejectEmailSubjectDefault: rejSubjDef,
      actionPlanningTaskRejectEmailBodyDefault: rejBodyDef,
      actionPlanningTaskRejectEmailSubjectTemplate: rejSubjTpl || rejSubjDef,
      actionPlanningTaskRejectEmailBodyTemplate: rejBodyTpl || rejBodyDef,
      approvalAssignmentEmailSubjectDefault: apvAssignSubjDef,
      approvalAssignmentEmailBodyDefault: apvAssignBodyDef,
      approvalAssignmentEmailSubjectTemplate: apvAssignSubjTpl || apvAssignSubjDef,
      approvalAssignmentEmailBodyTemplate: apvAssignBodyTpl || apvAssignBodyDef,
      approvalDecisionAcceptEmailSubjectDefault: apvAccSubjDef,
      approvalDecisionAcceptEmailBodyDefault: apvAccBodyDef,
      approvalDecisionAcceptEmailSubjectTemplate: apvAccSubjTpl || apvAccSubjDef,
      approvalDecisionAcceptEmailBodyTemplate: apvAccBodyTpl || apvAccBodyDef,
      approvalDecisionRejectEmailSubjectDefault: apvRejSubjDef,
      approvalDecisionRejectEmailBodyDefault: apvRejBodyDef,
      approvalDecisionRejectEmailSubjectTemplate: apvRejSubjTpl || apvRejSubjDef,
      approvalDecisionRejectEmailBodyTemplate: apvRejBodyTpl || apvRejBodyDef,
      implAssignmentEmailSubjectDefault: implAsgSubjDef,
      implAssignmentEmailBodyDefault: implAsgBodyDef,
      implAssignmentEmailSubjectTemplate: implAsgSubjTpl || implAsgSubjDef,
      implAssignmentEmailBodyTemplate: implAsgBodyTpl || implAsgBodyDef,
      implOwnerAcceptEmailSubjectDefault: implOwnAccSubjDef,
      implOwnerAcceptEmailBodyDefault: implOwnAccBodyDef,
      implOwnerAcceptEmailSubjectTemplate: implOwnAccSubjTpl || implOwnAccSubjDef,
      implOwnerAcceptEmailBodyTemplate: implOwnAccBodyTpl || implOwnAccBodyDef,
      implOwnerRejectEmailSubjectDefault: implOwnAccSubjDef,
      implOwnerRejectEmailBodyDefault: implOwnAccBodyDef,
      implOwnerRejectEmailSubjectTemplate: implOwnAccSubjTpl || implOwnAccSubjDef,
      implOwnerRejectEmailBodyTemplate: implOwnAccBodyTpl || implOwnAccBodyDef,
      verificationAssignmentEmailSubjectDefault: verAsgSubjDef,
      verificationAssignmentEmailBodyDefault: verAsgBodyDef,
      verificationAssignmentEmailSubjectTemplate: verAsgSubjTpl || verAsgSubjDef,
      verificationAssignmentEmailBodyTemplate: verAsgBodyTpl || verAsgBodyDef,
      verificationOutcomeEffectiveEmailSubjectDefault: verEffSubjDef,
      verificationOutcomeEffectiveEmailBodyDefault: verEffBodyDef,
      verificationOutcomeEffectiveEmailSubjectTemplate: verEffSubjTpl || verEffSubjDef,
      verificationOutcomeEffectiveEmailBodyTemplate: verEffBodyTpl || verEffBodyDef,
      verificationOutcomeNotEffectiveEmailSubjectDefault: verNeSubjDef,
      verificationOutcomeNotEffectiveEmailBodyDefault: verNeBodyDef,
      verificationOutcomeNotEffectiveEmailSubjectTemplate: verNeSubjTpl || verNeSubjDef,
      verificationOutcomeNotEffectiveEmailBodyTemplate: verNeBodyTpl || verNeBodyDef,
    };
  } catch (_e) {
    return buildCapaConfigFallback();
  }
}

export async function saveCapaConfig(payload, userData) {
  const statuses = Array.isArray(payload?.statuses)
    ? payload.statuses.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const sourceTypes = Array.isArray(payload?.sourceTypes)
    ? payload.sourceTypes.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const hasModulesField = Array.isArray(payload?.modules);
  const modules = hasModulesField
    ? payload.modules.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const rcaMethods = Array.isArray(payload?.rcaMethods)
    ? payload.rcaMethods.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const numberingFormat = String(payload?.numberingFormat || "CAPA-{YYYY}-{SEQ}").trim();
  const defaultSlaDays = Number(payload?.defaultSlaDays || 30);

  const emailSubject = String(payload?.actionPlanningEmailSubjectTemplate ?? "").trim();
  const emailBody = String(payload?.actionPlanningEmailBodyTemplate ?? "").trim();
  const accSubj = String(payload?.actionPlanningTaskAcceptEmailSubjectTemplate ?? "").trim();
  const accBody = String(payload?.actionPlanningTaskAcceptEmailBodyTemplate ?? "").trim();
  const rejSubj = String(payload?.actionPlanningTaskRejectEmailSubjectTemplate ?? "").trim();
  const rejBody = String(payload?.actionPlanningTaskRejectEmailBodyTemplate ?? "").trim();

  const apvAssignSubj = String(payload?.approvalAssignmentEmailSubjectTemplate ?? "").trim();
  const apvAssignBody = String(payload?.approvalAssignmentEmailBodyTemplate ?? "").trim();
  const apvAccSubj = String(payload?.approvalDecisionAcceptEmailSubjectTemplate ?? "").trim();
  const apvAccBody = String(payload?.approvalDecisionAcceptEmailBodyTemplate ?? "").trim();
  const apvRejSubj = String(payload?.approvalDecisionRejectEmailSubjectTemplate ?? "").trim();
  const apvRejBody = String(payload?.approvalDecisionRejectEmailBodyTemplate ?? "").trim();

  const implAsgSubj = String(payload?.implAssignmentEmailSubjectTemplate ?? "").trim();
  const implAsgBody = String(payload?.implAssignmentEmailBodyTemplate ?? "").trim();
  const implOwnAccSubj = String(payload?.implOwnerAcceptEmailSubjectTemplate ?? "").trim();
  const implOwnAccBody = String(payload?.implOwnerAcceptEmailBodyTemplate ?? "").trim();

  const verAsgSubj = String(payload?.verificationAssignmentEmailSubjectTemplate ?? "").trim();
  const verAsgBody = String(payload?.verificationAssignmentEmailBodyTemplate ?? "").trim();
  const verEffSubj = String(
    payload?.verificationOutcomeEffectiveEmailSubjectTemplate ?? "",
  ).trim();
  const verEffBody = String(payload?.verificationOutcomeEffectiveEmailBodyTemplate ?? "").trim();
  const verNeSubj = String(
    payload?.verificationOutcomeNotEffectiveEmailSubjectTemplate ?? "",
  ).trim();
  const verNeBody = String(
    payload?.verificationOutcomeNotEffectiveEmailBodyTemplate ?? "",
  ).trim();

  const requestPayload = {
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    NumberingFormat: numberingFormat || "CAPA-{YYYY}-{SEQ}",
    DefaultSlaDays: Number.isFinite(defaultSlaDays) ? defaultSlaDays : 30,
    StatusesJson: JSON.stringify(statuses),
    SourceTypesJson: JSON.stringify(sourceTypes),
    RcaMethodsJson: JSON.stringify(rcaMethods),
    ActionPlanningEmailSubjectTemplate: emailSubject || null,
    ActionPlanningEmailBodyTemplate: emailBody || null,
    ActionPlanningTaskAcceptEmailSubjectTemplate: accSubj || null,
    ActionPlanningTaskAcceptEmailBodyTemplate: accBody || null,
    ActionPlanningTaskRejectEmailSubjectTemplate: rejSubj || null,
    ActionPlanningTaskRejectEmailBodyTemplate: rejBody || null,
    ApprovalAssignmentEmailSubjectTemplate: apvAssignSubj || null,
    ApprovalAssignmentEmailBodyTemplate: apvAssignBody || null,
    ApprovalDecisionAcceptEmailSubjectTemplate: apvAccSubj || null,
    ApprovalDecisionAcceptEmailBodyTemplate: apvAccBody || null,
    ApprovalDecisionRejectEmailSubjectTemplate: apvRejSubj || null,
    ApprovalDecisionRejectEmailBodyTemplate: apvRejBody || null,
    ImplAssignmentEmailSubjectTemplate: implAsgSubj || null,
    ImplAssignmentEmailBodyTemplate: implAsgBody || null,
    ImplOwnerAcceptEmailSubjectTemplate: implOwnAccSubj || null,
    ImplOwnerAcceptEmailBodyTemplate: implOwnAccBody || null,
    ImplOwnerRejectEmailSubjectTemplate: implOwnAccSubj || null,
    ImplOwnerRejectEmailBodyTemplate: implOwnAccBody || null,
    VerificationAssignmentEmailSubjectTemplate: verAsgSubj || null,
    VerificationAssignmentEmailBodyTemplate: verAsgBody || null,
    VerificationOutcomeEffectiveEmailSubjectTemplate: verEffSubj || null,
    VerificationOutcomeEffectiveEmailBodyTemplate: verEffBody || null,
    VerificationOutcomeNotEffectiveEmailSubjectTemplate: verNeSubj || null,
    VerificationOutcomeNotEffectiveEmailBodyTemplate: verNeBody || null,
  };
  if (hasModulesField) {
    requestPayload.ModulesJson = JSON.stringify(modules);
  }
  const res = await postCapa(API_ENDPOINTS.CAPA.CONFIG_SAVE, requestPayload);

  return {
    success: true,
    message: res?.message || "CAPA configuration saved successfully.",
  };
}

/**
 * @param {string} capaId
 * @param {string} action — WORKFLOW_ACTIONS.*
 * @param {object} userData
 * @param {string} [reason] Optional auth-modal reason text; persisted as `Comments` on the
 *   transition row in `dbo.CAPAAuditLog` (proc `usp_CAPA_Activity_NEWUI`, `TransitionStatus`).
 */
export async function executeCapaTransition(capaId, action, userData, reason) {
  try {
    const { detail } = await fetchCapaById(capaId, userData);
    if (!detail) return { ok: false, message: "CAPA not found" };
    const capId =
      pickCapaId(detail) || (await toCapaGuidForModuleApi(capaId, userData));
    if (!capId) {
      return { ok: false, message: "Could not resolve CAPA identifier." };
    }
    const validationMessage = validateTransition(detail, action);
    if (validationMessage) return { ok: false, message: validationMessage };
    const next = getNextStatusAfterAction(action, detail.Status);
    if (!next) return { ok: false, message: "Invalid transition" };
    const trimmedReason = String(reason || "").trim();
    /* Same org resolution as module APIs / audit log — SP filters by CAPA.OrganizationRefId. */
    const organizationId = organizationIdForCapaModule(
      userData,
      capaModuleOptsFromDetail(detail, userData),
    );
    if (!organizationId) {
      return { ok: false, message: "Organization context is missing." };
    }
    if (!String(userData?.StaffId || "").trim()) {
      return { ok: false, message: "Staff context is missing." };
    }
    await postCapa(API_ENDPOINTS.CAPA.TRANSITION_STATUS, {
      OrganizationId: organizationId,
      CreatedBy: userData.StaffId,
      CapaId: capId,
      Status: next,
      TransitionAction: action,
      TransitionComment: trimmedReason || null,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e?.message || "Transition failed" };
  }
}

function hasValue(v) {
  if (v == null) return false;
  const s = String(v).trim();
  return s.length > 0 && s !== "—";
}

function validateTransition(detail, action) {
  if (action === WORKFLOW_ACTIONS.SUBMIT_INVESTIGATION) {
    if (!hasValue(detail.Title)) {
      return "Title is required before submitting investigation.";
    }
    if (!hasValue(detail.Description)) {
      return "Description is required before submitting investigation.";
    }
    return null;
  }
  if (action === WORKFLOW_ACTIONS.SUBMIT_RCA) {
    // Investigation details are captured as InvestigationSummary on this screen;
    // formal RootCauseSummary / RcaMethod are optional here and can be completed in the RCA step UI.
    return null;
  }
  // SUBMIT_ACTION_PLAN: no gate on action items; status advances to Action Planning where CAPs are added.
  if (action === WORKFLOW_ACTIONS.COMPLETE_CLOSURE) {
    if (String(detail.Status || "") !== "Closure") {
      return "CAPA must be in Closure before closing.";
    }
    const r = stripHtml(String(detail.ClosureRemarks || "")).trim();
    if (!r) {
      return "Enter final remarks before closing the CAPA.";
    }
    return null;
  }
  return null;
}

/**
 * @param {Record<string, unknown>} payload
 */
export async function saveCapa(payload, userData) {
  const merged = { ...payload };
  if (merged.CapaId == null && merged.Status == null) {
    merged.Status = INITIAL_CAPA_STATUS;
  }
  const requestPayload = normalizeCapaPayload(merged, userData);
  const resolvedCapaId = String(requestPayload.CapaId ?? "").trim();
  try {
    /* Use normalized id: empty string must not pick SAVE (would create a stray CAPA row). */
    const endpoint = resolvedCapaId ? API_ENDPOINTS.CAPA.UPDATE : API_ENDPOINTS.CAPA.SAVE;
    const res = await postCapa(endpoint, requestPayload);
    const ds = getApiDataset(res);
    const success = getFirstAvailableTable(ds, ["SuccessStory"]);
    const first = Array.isArray(success) && success[0] ? success[0] : {};
    return {
      success: true,
      CapaId: first.CapaId || resolvedCapaId || null,
      CapaNo: first.CapaNo || merged.CapaNo || null,
      Status: merged.Status || null,
      message: first.MSG || res.message || "Saved successfully.",
    };
  } catch (e) {
    throw new Error(e?.message || "Unable to save CAPA.");
  }
}

export async function removeCapa(capaId, payload, userData) {
  const reason = String(payload?.reason || "").trim();
  try {
    await postCapa(API_ENDPOINTS.CAPA.REMOVE, {
      OrganizationId: userData?.OrganizationId || "",
      CreatedBy: userData?.StaffId || "",
      CapaId: capaId,
      InactiveReason: reason,
    });
    return { CapaId: capaId, IsActive: 0, InactiveReason: reason };
  } catch (e) {
    throw new Error(e?.message || "Unable to remove CAPA.");
  }
}
